import { PageClassSelector } from '../../../components/layout/TeacherWorkspace';
import React, { useEffect, useState } from 'react';
import { preferredTeacherSection, rememberTeacherSection } from '../../../services/teacherSection';
import { Link } from 'react-router-dom';
import TeacherNav from '../../../components/TeacherNav';
import { OptionalAppShell } from '../../../components/layout/AppShell';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { sectionsApi } from '../../../services/apiV2';
import DailyCompletionMatrix from '../../../components/DailyCompletionMatrix';
import DailyBrief from '../../../components/DailyBrief';

const DailyCompletion: React.FC = () => {
  const redesignEnabled = isUiRedesignPageEnabled('daily-completion');
  const [sections, setSections] = useState<any[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showIncompleteOnly, setShowIncompleteOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [customDates, setCustomDates] = useState(false);
  useEffect(() => { rememberTeacherSection(selectedSectionId); }, [selectedSectionId]);

  useEffect(() => {
    loadSections();
  }, []);

  useEffect(() => {
    if (!selectedSectionId || customDates) return;
    const section = sections.find(row => row.id === selectedSectionId);
    let formatter: Intl.DateTimeFormat;
    try { formatter = new Intl.DateTimeFormat('en-US', { timeZone: section?.timezone || 'America/Boise', year: 'numeric', month: '2-digit', day: '2-digit' }); }
    catch { formatter = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Boise', year: 'numeric', month: '2-digit', day: '2-digit' }); }
    const parts = formatter.formatToParts(new Date());
    const part = (type: string) => parts.find(row => row.type === type)?.value || '';
    const today = `${part('year')}-${part('month')}-${part('day')}`;
    const weekAgo = new Date(`${today}T12:00:00Z`);
    weekAgo.setUTCDate(weekAgo.getUTCDate() - 6);
    setEndDate(today); setStartDate(weekAgo.toISOString().slice(0, 10));
  }, [sections, selectedSectionId, customDates]);

  const loadSections = async () => {
    setLoading(true);
    try {
      const response = await sectionsApi.list();
      // Handle paginated response - data is in results array
      const sectionsData = Array.isArray(response.data?.results)
        ? response.data.results.filter((section: any) => section.is_active !== false)
        : [];
      setSections(sectionsData);
      if (sectionsData.length > 0) {
        setSelectedSectionId(preferredTeacherSection(sectionsData));
      }
    } catch (err) {
      console.error('Error loading sections:', err);
      setSections([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <OptionalAppShell
      enabled={redesignEnabled}
      role="teacher"
      navItems={teacherV2NavItems('daily-completion')}
      title="Daily Completion"
    >
      {!redesignEnabled && <TeacherNav activeTab="daily-completion" />}
      <div className={redesignEnabled ? 'mx-auto max-w-6xl space-y-4' : 'mx-auto max-w-6xl p-6'}>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Teacher Attention</h1>
          <p className="text-gray-600 mt-1">
            Start with the students who need you now, then review completion details.
          </p>
        </div>

        {/* Brief context */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <PageClassSelector><div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Section
              </label>
              <select
                aria-label="Section"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedSectionId || ''}
                onChange={(e) => setSelectedSectionId(Number(e.target.value))}
                disabled={loading}
              >
                {loading ? (
                  <option>Loading...</option>
                ) : Array.isArray(sections) && sections.length > 0 ? (
                  sections.map((section) => (
                    <option key={section.id} value={section.id}>
                      {section.name}
                    </option>
                  ))
                ) : (
                  <option>No sections available</option>
                )}
              </select>
            </div></PageClassSelector>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Brief Date
              </label>
              <input
                aria-label="Brief Date"
                type="date"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={endDate}
                onChange={(e) => { setCustomDates(true); setEndDate(e.target.value); }}
              />
              <p className="mt-1 text-xs text-gray-500">Defaults to the class’s local date, not UTC.</p>
            </div>

          </div>
        </div>

        {/* Daily Brief */}
        {selectedSectionId && endDate && (
          <section aria-labelledby="daily-brief-heading">
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Daily Brief</p>
              <h2 id="daily-brief-heading" className="mt-1 text-xl font-bold text-gray-900">Who needs follow-up?</h2>
              <p className="mt-1 text-sm text-gray-600">Priorities combine current and older unresolved signals. Check each source date; use the active lesson workspace for live classroom context.</p>
            </div>
            <DailyBrief sectionId={selectedSectionId} targetDate={endDate} />
          </section>
        )}

        {/* Matrix */}
        {selectedSectionId && startDate && endDate && (
          <section aria-labelledby="completion-details-heading" className="border-t border-gray-200 pt-6">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">Reporting</p>
                <h2 id="completion-details-heading" className="mt-1 text-xl font-bold text-gray-900">Completion Details</h2>
                <p className="mt-1 text-sm text-gray-600">Review assignments alongside daily goal plans and wrap-ups. Daily goal work is tracked separately, not added to assignment totals.</p>
              </div>
              <Link
                to="/v2/teacher/reports"
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:border-indigo-300 hover:text-indigo-700"
              >
                Weekly &amp; archived reports
              </Link>
            </div>
            <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Start Date</label>
                  <input
                    aria-label="Start Date"
                    type="date"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={startDate}
                    onChange={(e) => { setCustomDates(true); setStartDate(e.target.value); }}
                  />
                </div>
                <div>
                  <span className="mb-1 block text-sm font-medium text-gray-700">Through</span>
                  <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">{endDate}</div>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={showIncompleteOnly}
                      onChange={(e) => setShowIncompleteOnly(e.target.checked)}
                    />
                    <span className="text-sm text-gray-700">Show Incomplete Only</span>
                  </label>
                </div>
              </div>
            </div>
            <DailyCompletionMatrix
              sectionId={selectedSectionId}
              startDate={startDate}
              endDate={endDate}
              showIncompleteOnly={showIncompleteOnly}
            />
          </section>
        )}

        {!selectedSectionId && (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <p className="text-gray-500">Select a section to view completion data</p>
          </div>
        )}
      </div>
    </OptionalAppShell>
  );
};

export default DailyCompletion;
